import axios, { type AxiosRequestConfig } from 'axios';
import { buildLoginUrl, getCurrentPathWithSearchAndHash, isAuthPage } from '@/lib/auth-redirect';
import { clearSessionQueries } from '@/lib/query-client';
import { getApiErrorMessage } from './error';

type AuthRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

export const skipAuthRefreshConfig = {
  _skipAuthRefresh: true,
} as AuthRequestConfig;

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  request: AuthRequestConfig;
}> = [];

function subscribeRefresh(
  resolve: (value: unknown) => void,
  reject: (reason?: unknown) => void,
  request: AuthRequestConfig,
) {
  refreshQueue.push({ resolve, reject, request });
}

function onRefreshed() {
  refreshQueue.forEach(({ resolve, reject, request }) => {
    api(request).then(resolve).catch(reject);
  });
  refreshQueue = [];
}

function onRefreshFailed(error: unknown) {
  refreshQueue.forEach(({ reject }) => reject(error));
  refreshQueue = [];
}

function redirectToLogin() {
  const pathname = window.location.pathname;
  if (isAuthPage(pathname)) {
    return;
  }

  const returnTo = getCurrentPathWithSearchAndHash();
  window.location.replace(buildLoginUrl(returnTo));
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true, // cookie-first
  timeout: 10000,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    error.message = getApiErrorMessage(error, error?.message);
    const originalRequest = error?.config as AuthRequestConfig | undefined;

    if (!originalRequest || error?.response?.status !== 401) {
      throw error;
    }

    // Requests explicitly marked to skip refresh should fail fast.
    if (originalRequest._skipAuthRefresh) {
      throw error;
    }

    const requestUrl = String(error?.config?.url ?? '');
    if (requestUrl.includes('/auth/refresh-token')) {
      redirectToLogin();
      throw error;
    }

    if (originalRequest._retry) {
      redirectToLogin();
      throw error;
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeRefresh(resolve, reject, originalRequest);
      });
    }

    isRefreshing = true;

    try {
      await api.post('/auth/refresh-token', {}, { _skipAuthRefresh: true } as AuthRequestConfig);
      onRefreshed();
      return api(originalRequest);
    } catch (refreshError) {
      // Update React state immediately. The hard redirect remains as a fallback,
      // but the current SPA must not keep rendering with stale permissions.
      clearSessionQueries();
      await handleRefreshFailure();
      onRefreshFailed(refreshError);
      redirectToLogin();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  },
);

async function handleRefreshFailure() {
  // Use a plain client to avoid interceptor recursion on logout.
  try {
    await axios.post(
      '/auth/logout',
      {},
      { baseURL: import.meta.env.VITE_API_URL, withCredentials: true, timeout: 10000 },
    );
  } catch {
    // ignore
  }
}
